// @ts-check

import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import taskService from '../../services/taskService';
import FileUpload from '../FileUpload';
import Modal from '../ui/Modal';

const TaskCreateModal = ({ isOpen, onClose, onSuccess, adminUsers = [], currentUser }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium',
    status: 'to_do',
    tags: '',
    estimated_hours: '',
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        title: '',
        description: '',
        assigned_to: '',
        due_date: '',
        priority: 'medium',
        status: 'to_do',
        tags: '',
        estimated_hours: '',
      });
      setUploadedFiles([]);
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare task data
      const taskData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        assigned_to: formData.assigned_to || null,
        due_date: formData.due_date || null,
        priority: formData.priority,
        status: formData.status,
        tags: formData.tags
          ? formData.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
          : null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
      };

      // Create task
      const createdTask = await taskService.createTask(taskData);

      // Add attachments if any files were uploaded
      if (uploadedFiles.length > 0 && createdTask.id) {
        for (const file of uploadedFiles) {
          try {
            await taskService.createAttachment(createdTask.id, {
              file_url: file.url,
              file_name: file.name,
              file_size: file.size,
              file_type: file.format,
            });
          } catch (attachError) {
            console.error('Error adding attachment:', attachError);
            // Continue with other attachments even if one fails
          }
        }
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating task:', error);
      setError(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal maxWidth="2xl" onClose={onClose} closeOnBackdrop={false} className="rounded-lg shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">Create New Task</h2>
        <button
          onClick={onClose}
          className="text-muted-foreground/70 hover:text-foreground transition-colors"
          disabled={isSubmitting}
        >
          <X size={24} />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="p-6 space-y-4">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter task title"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add task description"
            rows={4}
            disabled={isSubmitting}
          />
        </div>

        {/* Row: Assignee & Due Date */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Assign To</label>
            <select
              value={formData.assigned_to}
              onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            >
              <option value="">Unassigned</option>
              {adminUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Due Date</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Row: Priority & Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isSubmitting}
            >
              <option value="to_do">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="review">Review</option>
              <option value="done">Done</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Tags</label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Comma-separated tags (e.g., photography, urgent, website)"
            disabled={isSubmitting}
          />
          <p className="text-xs text-muted-foreground mt-1">Separate multiple tags with commas</p>
        </div>

        {/* Estimated Hours */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-1">Estimated Hours</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={formData.estimated_hours}
            onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Hours"
            disabled={isSubmitting}
          />
        </div>

        {/* File Attachments */}
        <div>
          <label className="block text-sm font-medium text-foreground/80 mb-3">Attachments</label>
          <FileUpload
            onUploadComplete={setUploadedFiles}
            uploadType="task-attachment"
            multiple={true}
            maxFiles={10}
            existingFiles={uploadedFiles}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Creating...' : 'Create Task'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-6 py-2 border border-input text-foreground/80 rounded-lg hover:bg-muted/50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TaskCreateModal;
